import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { useEffect, useRef } from "react";
import { PnlCategoriesEnum } from "types/enums";
import { DataDumpAggregateType } from "types/types";
import {
  useSummaryGroup,
  useWorkScheduleIdOnCurrentPage,
} from "utils/customHooks/genericHooks";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";

/** filter state for process tiles in DashboardPages > ProcessTilesPanel */
export type ProcessTilesFilterState = {
  filterByCategory: (PnlCategoriesEnum | "SELECT_ALL")[];
  filterByTagNames: string[] | null;
  filterByTopLosses: string[] | null;
  sortBy: (SortableMetric & { order: "asc" | "desc" }) | null;
  groupBy: GroupableMetric | null;
  searchText: string | null;
  searchTextDisplay: string;
  selectedProcessNames: string[];
};

export const INITIAL_PROCESS_TILES_FILTER_STATE: ProcessTilesFilterState = {
  filterByCategory: [
    "SELECT_ALL",
    PnlCategoriesEnum.producing,
    PnlCategoriesEnum.planned,
    PnlCategoriesEnum.unplanned,
    PnlCategoriesEnum.waiting,
    PnlCategoriesEnum.unknown,
    PnlCategoriesEnum.notScheduled,
    PnlCategoriesEnum.uncollected,
  ],
  filterByTagNames: null, // null means no filter (no chip) instead of "all tags"
  filterByTopLosses: null, // null means no filter (no chip) instead of all losses
  sortBy: null,
  groupBy: null,
  searchText: null,
  searchTextDisplay: "",
  selectedProcessNames: ["SELECT_ALL"],
};

const processTilesFilterStateAtom = atomWithStorage<ProcessTilesFilterState>(
  "processTilesFilterStateAtom",
  INITIAL_PROCESS_TILES_FILTER_STATE
);

export const useProcessTilesFilterState = () => {
  return useAtom(processTilesFilterStateAtom);
};

export const useClearProcessTilesFilterStateWhenCkeyChanges = () => {
  const [, setFilterState] = useProcessTilesFilterState();
  const ckey = useCustomerKey();
  const prevCkey = useRef(null as string | null);

  // when we change customers, clear the filter state
  useEffect(() => {
    if (ckey && ckey !== prevCkey.current) {
      setFilterState(INITIAL_PROCESS_TILES_FILTER_STATE);
    }
    // track previous ckey but ignore nulls
    if (ckey) {
      prevCkey.current = ckey;
    }
  }, [ckey, setFilterState]);

  const summaryGroup = useSummaryGroup();
  const prevSummaryGroup = useRef(null as string | null);
  const workScheduleId = useWorkScheduleIdOnCurrentPage();
  const prevWorkSchedule = useRef(null as string | null);

  // when we change groups or schedules, clear any selected processes
  useEffect(() => {
    if (summaryGroup && summaryGroup !== prevSummaryGroup.current) {
      setFilterState(prev => ({
        ...prev,
        selectedProcessNames:
          INITIAL_PROCESS_TILES_FILTER_STATE.selectedProcessNames,
      }));
    }
    // track previous group and schedule but ignore nulls
    if (summaryGroup) {
      prevSummaryGroup.current = summaryGroup;
    }
  }, [summaryGroup, setFilterState]);

  useEffect(() => {
    if (workScheduleId && workScheduleId !== prevWorkSchedule.current) {
      setFilterState(prev => ({
        ...prev,
        selectedProcessNames:
          INITIAL_PROCESS_TILES_FILTER_STATE.selectedProcessNames,
      }));
    }
    // track previous group and schedule but ignore nulls
    if (workScheduleId) {
      prevWorkSchedule.current = workScheduleId;
    }
  }, [workScheduleId, setFilterState]);
};

/** menu item in the GroupByDropdown */
export type GroupableMetric = {
  label: string;
  customKey?: "pnlCategory" | "group";
  order?: "asc" | "desc" | null;
  source: "askr" | "midgard";
};
export const GROUPABLE_METRICS: GroupableMetric[] = [
  { label: "Group", customKey: "group", source: "askr" },
  { label: "Status", customKey: "pnlCategory", source: "askr" },
];
/** menu item in the SortByDropdown */
export type SortableMetric = {
  label: string;
  dataDumpKey?: keyof DataDumpAggregateType;
  /** each customKey requires a custom calculation */
  customKey?: "aheadBehind" | "scheduledTime" | "pnlCategory";
  order?: "asc" | "desc" | null;
  /** askr metrics are available for all customers; midgard metrics can only be calculated from dataDumpAggregates */
  source: "askr" | "midgard";
};
export const SORTABLE_METRICS: SortableMetric[] = [
  // status
  {
    label: "Status",
    customKey: "pnlCategory",
    source: "askr",
  },
  // metrics
  {
    label: "Availability",
    dataDumpKey: "availabilityPercent",
    source: "askr",
  },
  { label: "TEEP", dataDumpKey: "teepPercent", source: "askr" },
  { label: "OEE", dataDumpKey: "oeePercent", source: "midgard" },
  { label: "Uptime", dataDumpKey: "uptimePercent", source: "askr" },
  // times
  { label: "Waiting Time", dataDumpKey: "waitingTime", source: "midgard" },
  {
    label: "Unplanned Time",
    dataDumpKey: "unplannedTime",
    source: "midgard",
  },
  {
    label: "Not Scheduled",
    dataDumpKey: "notScheduledTime",
    source: "midgard",
  },
  {
    label: "Scheduled Time",
    customKey: "scheduledTime",
    source: "midgard",
  },
  { label: "Unknown Time", dataDumpKey: "unknownTime", source: "midgard" },
  { label: "Ahead/Behind", customKey: "aheadBehind", source: "midgard" },
];
